import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faBolt, faCoins } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

// Download any celebration animation from LottieFiles and put it here
import celebrationAnimation from "../../../../assets/lottie/lesson-complete.json";
import { useLanguage } from "../../../../context/LanguageProvider";
import FadeIn from "../../../../shared/components/animation/FadeIn";
import LottieView from "../../../../shared/components/animation/LottieView";
import useCountUp from "../../../../shared/hooks/useCountUp";
import { formatNumber } from "../../../../shared/utils/format";
import { LESSON_MESSAGES } from "../../messages";
import ContinueButton from "./ContinueButton";

interface RewardCardProps {
    icon: IconDefinition;
    label: string;
    value: number;
    delay: number;
    iconClassName: string;
    cardClassName: string;
}

// A single reward card: icon, animated number and label
const RewardCard = ({ icon, label, value, delay, iconClassName, cardClassName }: RewardCardProps) => {
    const { lang } = useLanguage();
    const animatedValue = useCountUp(value, 1200, delay + 300);

    return (
        <FadeIn delay={delay}>
            <div
                className={classNames(
                    "flex flex-col items-center gap-2 rounded-2xl border p-5 shadow-sm",
                    cardClassName
                )}
            >
                <div
                    className={classNames(
                        "flex h-14 w-14 items-center justify-center rounded-full text-2xl",
                        iconClassName
                    )}
                >
                    <FontAwesomeIcon icon={icon} />
                </div>

                <span className="text-3xl font-extrabold text-gray-900">
                    {formatNumber(animatedValue, lang)}
                </span>

                <span className="text-sm font-medium text-gray-500">{label}</span>
            </div>
        </FadeIn>
    );
};

interface LessonCompleteProps {
    credit?: number; // Earned in THIS completion
    xp?: number;
    showRewards?: boolean; // False when the lesson was already completed before
    onContinue: () => void;
}

// Shown after the learner completes the last lesson screen
const LessonComplete = ({ credit = 0, xp = 0, showRewards = true, onContinue }: LessonCompleteProps) => (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-amber-50 via-white to-white px-6 py-10">
        <LottieView animationData={celebrationAnimation} className="h-64 w-64 sm:h-80 sm:w-80" />

        <FadeIn delay={200} className="text-center">
            <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
                {LESSON_MESSAGES.lessonComplete}
            </h2>

            <p className="mt-3 text-lg text-gray-500">
                {showRewards ? LESSON_MESSAGES.rewardsText : LESSON_MESSAGES.noRewardsText}
            </p>
        </FadeIn>

        {/* Rewards (only for a first-time completion) */}
        {showRewards && (
            <div className="mt-10 grid w-full max-w-md grid-cols-2 gap-4">
                <RewardCard
                    icon={faCoins}
                    label={LESSON_MESSAGES.credit}
                    value={credit}
                    delay={500}
                    cardClassName="border-amber-200 bg-amber-50"
                    iconClassName="bg-amber-100 text-amber-500"
                />

                <RewardCard
                    icon={faBolt}
                    label={LESSON_MESSAGES.xp}
                    value={xp}
                    delay={700}
                    cardClassName="border-indigo-200 bg-indigo-50"
                    iconClassName="bg-indigo-100 text-indigo-500"
                />
            </div>
        )}

        <FadeIn delay={showRewards ? 1000 : 500} className="mt-10 w-full max-w-md">
            <ContinueButton onClick={onContinue} colorClassName="bg-green-600 hover:bg-green-500" />
        </FadeIn>
    </div>
);

export default LessonComplete;